import { useId, useMemo } from 'react';
import { Sigil } from '@sorrem-client/ui/components';
import type { UISigilEnhancements } from '@sorrem-client/types';
import type { SkillTreeModel } from './skill-tree-model';
import {
    SKILL_TREE_BOUNDS,
    SKILL_TREE_SIGIL_POSITION,
    SKILL_TREE_SIGIL_SIZE,
} from './skill-tree-layout';
import { toDialSelectionId, toGroupSelectionId, SIGIL_SELECTION_ID, buildSkillTreeSvgIds, toSvgIdPrefix } from './skill-tree-ids';
import { TEXT_SHADOW } from './skill-tree-palette';
import { SkillTreeDial } from './SkillTreeDial';
import type { SkillTreeIntroSchedule } from './skill-tree-intro';
import { SkillTreeGroupNode } from './SkillTreeGroupNode';
import { SkillTreeEmblemGlowFilter } from './SkillTreeLineEmblem';
import { SkillTreeIntroPart } from './SkillTreeIntroPart';
import { SkillTreeLinks } from './SkillTreeLinks';
import { SkillTreeNode } from './SkillTreeNode';
import styles from './SkillTreeScene.module.scss';

interface SkillTreeSceneProps {
    readonly model: SkillTreeModel;
    /** Seeds the sigil's knot: the character id. */
    readonly sigilSeed: string;
    readonly hoveredId: string | null;
    readonly selectedId: string | null;
    readonly isReducedMotion: boolean;
    /**
     * The Skills tab intro's schedule while it plays (growing out of the
     * sigil, parent to child, or shrinking back into it); the intro's clock,
     * from context, poses each part (see `skill-tree-intro.ts`).
     */
    readonly intro: SkillTreeIntroSchedule | undefined;
}

/** The sigil's click target: its medallion, inside the 240-unit box. */
const SIGIL_HIT_RADIUS = 96;

const NO_SIGIL_ENHANCEMENTS: UISigilEnhancements = { martialTier: 0, veilMilestones: 0, craftingStage: 0 };

/**
 * Everything on the tree, drawn in tree units on one SVG that `SkillTree`
 * positions and scales: connectors, the sigil, the three dials, the
 * grouping roots, and every skill node.
 */
export function SkillTreeScene({ model, sigilSeed, hoveredId, selectedId, isReducedMotion, intro }: SkillTreeSceneProps): JSX.Element {
    const { min, max } = SKILL_TREE_BOUNDS;
    const width = max.x - min.x;
    const height = max.y - min.y;
    const sigilBox = {
        x: SKILL_TREE_SIGIL_POSITION.x - SKILL_TREE_SIGIL_SIZE / 2,
        y: SKILL_TREE_SIGIL_POSITION.y - SKILL_TREE_SIGIL_SIZE / 2,
        size: SKILL_TREE_SIGIL_SIZE,
    };
    // SVG ids are document-global: prefix this scene's own, so no other SVG resolves them.
    const idPrefix = toSvgIdPrefix(useId(), 'skill-tree');
    const svgIds = useMemo(() => buildSkillTreeSvgIds(idPrefix), [idPrefix]);

    return (
        <svg
            className={styles.skillTreeScene}
            style={{ left: min.x, top: min.y, width, height }}
            viewBox={`${min.x} ${min.y} ${width} ${height}`}
        >
            <defs>
                {/* Keeps labels readable over any part of the sky: a tight dark shadow plus a wide soft one. */}
                <filter id={svgIds.textShadow} x="-20%" y="-50%" width="140%" height="200%">
                    <feDropShadow dx="0" dy="2" stdDeviation="2" floodColor={TEXT_SHADOW} floodOpacity="0.95" result="tight" />
                    <feDropShadow in="tight" dx="0" dy="0" stdDeviation="7" floodColor={TEXT_SHADOW} floodOpacity="0.8" />
                </filter>
                {/* The soft glow under every line-art emblem's strokes. */}
                <SkillTreeEmblemGlowFilter id={svgIds.emblemGlow} />
            </defs>

            <SkillTreeLinks model={model} intro={intro} svgIds={svgIds} />

            {/*
              * During the intro the base sigil pulses as the reveal starts, and its
              * enhancements settle in over it as the dials finish. Outside it the
              * same wrappers stay, idle, so the sigil is never remounted.
              */}
            <SkillTreeIntroPart window={intro?.sigilPulse} motion="pulse" origin={SKILL_TREE_SIGIL_POSITION}>
                {intro && <Sigil seed={sigilSeed} enhancements={NO_SIGIL_ENHANCEMENTS} x={sigilBox.x} y={sigilBox.y} size={sigilBox.size} />}
                <SkillTreeIntroPart window={intro?.sigilSettle} motion="fade">
                    <Sigil seed={sigilSeed} enhancements={model.sigil} x={sigilBox.x} y={sigilBox.y} size={sigilBox.size} />
                </SkillTreeIntroPart>
            </SkillTreeIntroPart>
            <circle
                className={styles.hitTarget}
                cx={SKILL_TREE_SIGIL_POSITION.x}
                cy={SKILL_TREE_SIGIL_POSITION.y}
                r={SIGIL_HIT_RADIUS}
                data-node-id={SIGIL_SELECTION_ID}
            />

            {model.dials.map((dial) => {
                const id = toDialSelectionId(dial.placement.branch);
                return (
                    <SkillTreeDial
                        key={id}
                        dial={dial}
                        isHovered={hoveredId === id}
                        isSelected={selectedId === id}
                        svgIds={svgIds}
                        intro={intro}
                    />
                );
            })}

            {model.groups.map((group) => {
                const id = toGroupSelectionId(group.placement.id);
                return (
                    <SkillTreeGroupNode
                        key={id}
                        group={group}
                        isHovered={hoveredId === id}
                        isSelected={selectedId === id}
                        svgIds={svgIds}
                        intro={intro}
                    />
                );
            })}

            {model.nodes.map((node) => (
                <SkillTreeNode
                    key={node.placement.id}
                    node={node}
                    isHovered={hoveredId === node.placement.id}
                    isSelected={selectedId === node.placement.id}
                    isReducedMotion={isReducedMotion}
                    svgIds={svgIds}
                    intro={intro}
                />
            ))}
        </svg>
    );
}
